<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMessageContext } from '../provider.js';
import { formatBytes } from 'shared/helpers/FileHelper';
import BaseBubble from './Base.vue';
import FileIcon from 'next/icon/FileIcon.vue';
import Icon from 'next/icon/Icon.vue';

const { attachments } = useMessageContext();
const { t } = useI18n();
const attachment = computed(() => attachments.value[0] || {});
const url = computed(() => attachment.value.dataUrl || '');
const fileName = computed(() => {
  const name = url.value.split(/[?#]/)[0].split('/').pop();
  if (!name) return t('CONVERSATION.UNKNOWN_FILE_TYPE');
  try {
    return decodeURIComponent(name);
  } catch {
    return name;
  }
});
const fileType = computed(() => {
  const extension = attachment.value.extension;
  if (extension) return extension.replace(/^\./, '').toLowerCase();
  return fileName.value.includes('.')
    ? fileName.value.split('.').pop().toLowerCase()
    : '';
});
const fileDetails = computed(() =>
  [
    fileType.value.toUpperCase(),
    attachment.value.fileSize > 0 ? formatBytes(attachment.value.fileSize) : '',
  ]
    .filter(Boolean)
    .join(' · ')
);
</script>

<template>
  <BaseBubble
    class="w-72 !max-w-full p-2"
    meta-padding-class="px-1 pt-1"
    data-bubble-name="attachment"
  >
    <div class="flex min-w-0 items-center gap-2.5 rounded-lg bg-black/10 p-2.5">
      <div
        class="grid size-10 shrink-0 place-items-center rounded-lg bg-white/15"
      >
        <FileIcon :file-type="fileType" class="size-6" />
      </div>
      <div class="min-w-0 flex-1">
        <p
          class="mb-0 truncate text-sm font-medium text-current"
          :title="fileName"
        >
          {{ fileName }}
        </p>
        <p v-if="fileDetails" class="mb-0 mt-1 text-xs text-current opacity-80">
          {{ fileDetails }}
        </p>
      </div>
      <a
        v-if="url"
        v-tooltip="$t('CONVERSATION.DOWNLOAD')"
        :href="url"
        :aria-label="`${$t('CONVERSATION.DOWNLOAD')}: ${fileName}`"
        rel="noreferrer noopener nofollow"
        target="_blank"
        class="grid size-8 shrink-0 place-items-center rounded-full border border-current/30 text-current hover:bg-black/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-current"
      >
        <Icon icon="i-lucide-download" class="size-4" />
      </a>
    </div>
  </BaseBubble>
</template>
